import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { X, Plus } from "lucide-react";

const empty = {
  name: "",
  tagline: "",
  location: "",
  description: "",
  price_per_night: 0,
  bedrooms: 1,
  bathrooms: 1,
  max_guests: 2,
  beds: 1,
  amenities: [],
  images: [],
  featured: false,
  active: true,
};

export default function AdminPropertyForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [imgInput, setImgInput] = useState("");
  const [amenityInput, setAmenityInput] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/properties/${id}`)
      .then((r) => setForm({ ...empty, ...r.data }))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addImage = () => {
    if (!imgInput.trim()) return;
    set("images", [...form.images, imgInput.trim()]);
    setImgInput("");
  };
  const removeImage = (i) =>
    set(
      "images",
      form.images.filter((_, idx) => idx !== i),
    );
  const addAmenity = () => {
    if (!amenityInput.trim()) return;
    set("amenities", [...form.amenities, amenityInput.trim()]);
    setAmenityInput("");
  };
  const removeAmenity = (i) =>
    set(
      "amenities",
      form.amenities.filter((_, idx) => idx !== i),
    );

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price_per_night: parseFloat(form.price_per_night) || 0,
      bedrooms: parseInt(form.bedrooms) || 1,
      bathrooms: parseFloat(form.bathrooms) || 1,
      max_guests: parseInt(form.max_guests) || 1,
      beds: parseInt(form.beds) || 1,
    };
    try {
      if (isEdit) {
        await api.put(`/properties/${id}`, payload);
        toast.success("Property updated.");
      } else {
        await api.post("/properties", payload);
        toast.success("Property created.");
      }
      nav("/admin/properties");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-[#A3A3A3]">Loading…</p>;

  return (
    <div data-testid="admin-property-form-page">
      <p className="overline mb-2">{isEdit ? "Edit" : "New"} property</p>
      <h1 className="font-serif-luxe text-5xl leading-none">
        {isEdit ? form.name || "Untitled" : "Add a property"}
      </h1>

      <form
        onSubmit={submit}
        data-testid="admin-property-form"
        className="mt-10 space-y-8 border border-[#F5F5DC]/10 bg-[#1C1C1E] p-8"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Property name">
            <Input
              data-testid="pf-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              className="bg-[#121212] border-[#F5F5DC]/10"
            />
          </Field>
          <Field label="Location">
            <Input
              data-testid="pf-location"
              value={form.location}
              onChange={(e) => set("location", e.target.value)}
              required
              className="bg-[#121212] border-[#F5F5DC]/10"
              placeholder="City, Country"
            />
          </Field>
          <Field label="Tagline" full>
            <Input
              data-testid="pf-tagline"
              value={form.tagline}
              onChange={(e) => set("tagline", e.target.value)}
              className="bg-[#121212] border-[#F5F5DC]/10"
              placeholder="One-line description shown on cards"
            />
          </Field>
          <Field label="Description" full>
            <Textarea
              data-testid="pf-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={5}
              required
              className="bg-[#121212] border-[#F5F5DC]/10"
            />
          </Field>
          <Field label="Price per night ($)">
            <Input
              data-testid="pf-price"
              type="number"
              min={0}
              value={form.price_per_night}
              onChange={(e) => set("price_per_night", e.target.value)}
              required
              className="bg-[#121212] border-[#F5F5DC]/10"
            />
          </Field>
          <Field label="Max guests">
            <Input
              data-testid="pf-guests"
              type="number"
              min={1}
              value={form.max_guests}
              onChange={(e) => set("max_guests", e.target.value)}
              className="bg-[#121212] border-[#F5F5DC]/10"
            />
          </Field>
          <Field label="Bedrooms">
            <Input
              data-testid="pf-bedrooms"
              type="number"
              min={0}
              value={form.bedrooms}
              onChange={(e) => set("bedrooms", e.target.value)}
              className="bg-[#121212] border-[#F5F5DC]/10"
            />
          </Field>
          <Field label="Bathrooms">
            <Input
              data-testid="pf-bathrooms"
              type="number"
              min={0}
              step="0.5"
              value={form.bathrooms}
              onChange={(e) => set("bathrooms", e.target.value)}
              className="bg-[#121212] border-[#F5F5DC]/10"
            />
          </Field>
          <Field label="Beds">
            <Input
              data-testid="pf-beds"
              type="number"
              min={0}
              value={form.beds}
              onChange={(e) => set("beds", e.target.value)}
              className="bg-[#121212] border-[#F5F5DC]/10"
            />
          </Field>
        </div>

        {/* Images */}
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
            Photo URLs
          </Label>
          <div className="mt-3 flex gap-2">
            <Input
              data-testid="pf-image-input"
              value={imgInput}
              onChange={(e) => setImgInput(e.target.value)}
              placeholder="https://…"
              className="bg-[#121212] border-[#F5F5DC]/10"
            />
            <Button
              type="button"
              onClick={addImage}
              data-testid="pf-add-image"
              className="bg-[#D4AF37] text-[#121212] hover:bg-[#B3922E] rounded-none"
            >
              <Plus size={16} />
            </Button>
          </div>
          {form.images.length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {form.images.map((src, i) => (
                <div key={i} className="relative aspect-[4/3] group">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 p-1 bg-[#121212]/80 hover:bg-[#FF453A] text-[#F5F5DC]"
                    data-testid={`pf-remove-image-${i}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Amenities */}
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
            Amenities
          </Label>
          <div className="mt-3 flex gap-2">
            <Input
              data-testid="pf-amenity-input"
              value={amenityInput}
              onChange={(e) => setAmenityInput(e.target.value)}
              placeholder="e.g. Infinity pool"
              className="bg-[#121212] border-[#F5F5DC]/10"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addAmenity();
                }
              }}
            />
            <Button
              type="button"
              onClick={addAmenity}
              data-testid="pf-add-amenity"
              className="bg-[#D4AF37] text-[#121212] hover:bg-[#B3922E] rounded-none"
            >
              <Plus size={16} />
            </Button>
          </div>
          {form.amenities.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {form.amenities.map((a, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#F5F5DC]/10 text-sm"
                >
                  {a}
                  <button
                    type="button"
                    onClick={() => removeAmenity(i)}
                    className="hover:text-[#FF453A]"
                    data-testid={`pf-remove-amenity-${i}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Toggles */}
        <div className="flex flex-wrap gap-8 pt-4 border-t border-[#F5F5DC]/10">
          <div className="flex items-center gap-3">
            <Switch
              id="featured"
              checked={form.featured}
              onCheckedChange={(v) => set("featured", v)}
              data-testid="pf-featured"
            />
            <Label htmlFor="featured" className="text-sm cursor-pointer">
              Featured on homepage
            </Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="active"
              checked={form.active}
              onCheckedChange={(v) => set("active", v)}
              data-testid="pf-active"
            />
            <Label htmlFor="active" className="text-sm cursor-pointer">
              Live on public site
            </Label>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            disabled={saving}
            data-testid="pf-submit"
            className="px-8 h-11 bg-[#D4AF37] text-[#121212] hover:bg-[#B3922E] text-xs tracking-[0.24em] uppercase rounded-none"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create property"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => nav("/admin/properties")}
            className="border-[#F5F5DC]/20 hover:border-[#F5F5DC]/50 rounded-none bg-transparent"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
